// src/pages/personal/settings/PromotionsDiscountsSection/SeasonalEvents.tsx
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, Plus, Edit2, Trash2, Check, X } from 'lucide-react';
import { TEXT_STYLES } from '@/theme/typography';
import { COMPONENT_STYLES } from '@/theme/components';
import { COLORS } from '@/theme/colors';
import useBusinessCurrency from '@/hooks/useBusinessCurrency';
import { SeasonalEvent } from './types';
import { Card, Flex, Grid } from '@/components/ui/Layout';
import { Table, TableColumn } from '@/components/ui/Table';
import { useResponsive } from '@/hooks/useResponsive';

interface SeasonalEventsProps {
  seasonalEvents: {
    [name: string]: SeasonalEvent;
  };
  onChange: (data: any) => void;
}

interface EventFormData {
  name: string;
  description: string;
  startDate: string;
  endDate: string;
  active: boolean;
  recurring: boolean;
  interval: 'weekly' | 'monthly' | 'yearly';
  type: 'percentage' | 'fixed_amount';
  value: number;
}

// Тип для строки таблицы сезонных событий
interface EventTableRow extends SeasonalEvent {
  name: string;
}

const initialFormData: EventFormData = {
  name: '',
  description: '',
  startDate: new Date().toISOString().split('T')[0],
  endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
  active: true,
  recurring: false,
  interval: 'yearly',
  type: 'percentage',
  value: 0
};

const SeasonalEvents: React.FC<SeasonalEventsProps> = ({ seasonalEvents, onChange }) => {
  // ИСПРАВЛЕНО: Указываем используемые пространства имен
  const { t } = useTranslation(['promotions', 'common']);
  const { currencySymbol } = useBusinessCurrency();
  const [isAdding, setIsAdding] = useState(false);
  const [editingEvent, setEditingEvent] = useState<string | null>(null);
  const [formData, setFormData] = useState<EventFormData>(initialFormData);
  const { isMobile } = useResponsive();
  
  // Преобразуем события в массив для отображения
  const eventsList: EventTableRow[] = Object.entries(seasonalEvents).map(([name, data]) => ({
    name,
    ...data
  }));

  // Обработчик изменения поля формы
  const handleFormChange = (field: keyof EventFormData, value: any) => {
    setFormData((prev: EventFormData) => ({
      ...prev,
      [field]: value
    }));
  };

  // Открытие формы добавления нового события
  const handleAddNew = () => {
    setFormData(initialFormData);
    setIsAdding(true);
    setEditingEvent(null);
  };

  // Открытие формы редактирования существующего события
  const handleEdit = (name: string) => {
    const eventData = seasonalEvents[name];
    
    setFormData({
      name,
      description: eventData.description,
      startDate: eventData.period[0].split('T')[0],
      endDate: eventData.period[1].split('T')[0],
      active: eventData.active,
      recurring: eventData.recurrence?.recurring || false,
      interval: eventData.recurrence?.interval || 'yearly',
      type: eventData.type,
      value: eventData.value
    });
    
    setEditingEvent(name);
    setIsAdding(true);
  };

  // Удаление события
  const handleDelete = (name: string) => {
    const updatedEvents = { ...seasonalEvents };
    delete updatedEvents[name];
    onChange(updatedEvents);
  };

  // Сохранение события
  const handleSave = () => {
    if (!formData.name.trim()) {
      // Используем window.confirm вместо alert
      window.confirm(t('promotions:promotions.event_name_required'));
      return;
    }

    const newEvent: SeasonalEvent = {
      description: formData.description,
      period: [
        `${formData.startDate}T00:00:00Z`,
        `${formData.endDate}T23:59:59Z`
      ],
      active: formData.active,
      recurrence: formData.recurring ? {
        recurring: true,
        interval: formData.interval
      } : undefined,
      type: formData.type,
      value: formData.value
    };

    const updatedEvents = { ...seasonalEvents };
    
    if (editingEvent && editingEvent !== formData.name) {
      // Если название было изменено, удаляем старое и добавляем новое
      delete updatedEvents[editingEvent];
    }
    
    updatedEvents[formData.name] = newEvent;
    onChange(updatedEvents);
    
    // Сбрасываем состояние формы
    setIsAdding(false);
    setEditingEvent(null);
    setFormData(initialFormData);
  };

  // Отмена добавления/редактирования
  const handleCancel = () => {
    setIsAdding(false);
    setEditingEvent(null);
    setFormData(initialFormData);
  };

  // Определяем колонки для таблицы событий
  const columns: TableColumn<EventTableRow>[] = [
    {
      key: 'name',
      title: t('promotions:promotions.event_name'),
      width: 'md',
      render: (item) => <span className="font-medium">{item.name}</span>
    },
    {
      key: 'description',
      title: t('promotions:promotions.description'),
      width: 'md'
    },
    {
      key: 'period',
      title: t('promotions:promotions.period'),
      width: 'lg',
      render: (item) => (
        <div>
          {`${new Date(item.period[0]).toLocaleDateString()} - ${new Date(item.period[1]).toLocaleDateString()}`}
          {item.recurrence?.recurring && (
            <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
              {t(`promotions:promotions.${item.recurrence.interval}`)}
            </span>
          )}
        </div>
      )
    },
    {
      key: 'discount',
      title: t('promotions:promotions.discount'),
      width: 'md',
      render: (item) => (
        <span>{item.value} {item.type === 'percentage' ? '%' : currencySymbol}</span>
      )
    },
    {
      key: 'status',
      title: t('promotions:promotions.status'),
      width: 'md',
      render: (item) => (
        <span className={`px-2 py-1 rounded text-xs ${item.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
          {item.active ? t('promotions:promotions.active') : t('promotions:promotions.inactive')}
        </span>
      )
    },
    {
      key: 'actions',
      title: t('common:actions'),
      width: 'sm',
      textAlign: 'right',
      render: (item) => (
        <Flex justify="end" gap="xs">
          <button
            onClick={() => handleEdit(item.name)}
            className="text-blue-600 hover:text-blue-800"
            title={t('common:edit')}
          >
            <Edit2 className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleDelete(item.name)}
            className="text-red-600 hover:text-red-800"
            title={t('common:delete')}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </Flex>
      )
    }
  ];

  return (
    <Card 
      padding={isMobile ? "sm" : "md"} 
      className="border border-gray-200"
    >
      <Flex justify="between" align="center" className="mb-4">
        <Flex align="center" gap="xs">
          <Calendar className={`w-5 h-5 text-primary`} />
          <h3 className={TEXT_STYLES.h3}>{t('promotions:promotions.seasonal_events')}</h3>
        </Flex>
        
        {!isAdding && (
          <button
            onClick={handleAddNew}
            className={`${COMPONENT_STYLES.button.base} ${COMPONENT_STYLES.button.sizes.sm} ${COMPONENT_STYLES.button.secondary}`}
          >
            <Plus className="w-4 h-4 mr-1" />
            {t('common:add')}
          </button>
        )}
      </Flex>
      
      <p className={`mb-4 text-sm text-[${COLORS.secondary}]`}>
        {t('promotions:promotions.seasonal_events_description')}
      </p>
      
      {/* Форма добавления/редактирования события */}
      {isAdding && (
        <Card 
          padding="md" 
          className="bg-gray-50 mb-4 border border-gray-200"
        >
          <h3 className={`${TEXT_STYLES.h3} mb-3`}>
            {editingEvent ? t('promotions:promotions.edit_event') : t('promotions:promotions.add_event')}
          </h3>
          
          <Grid 
            cols={isMobile ? 1 : 2} 
            gap="md" 
            className="mb-4"
            mobileCols={1}
          >
            <div>
              <label className={COMPONENT_STYLES.form.label}>
                {t('promotions:promotions.event_name')} *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleFormChange('name', e.target.value)}
                className={COMPONENT_STYLES.form.input}
                placeholder={t('promotions:promotions.event_name_placeholder')}
                required
              />
            </div>
            
            <div>
              <label className={COMPONENT_STYLES.form.label}>
                {t('promotions:promotions.description')}
              </label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => handleFormChange('description', e.target.value)}
                className={COMPONENT_STYLES.form.input}
                placeholder={t('promotions:promotions.description_placeholder')}
              />
            </div>
            
            <div>
              <label className={COMPONENT_STYLES.form.label}>
                {t('promotions:promotions.start_date')}
              </label>
              <input
                type="date"
                value={formData.startDate}
                onChange={(e) => handleFormChange('startDate', e.target.value)}
                className={COMPONENT_STYLES.form.input}
              />
            </div>
            
            <div>
              <label className={COMPONENT_STYLES.form.label}>
                {t('promotions:promotions.end_date')}
              </label>
              <input
                type="date"
                value={formData.endDate}
                onChange={(e) => handleFormChange('endDate', e.target.value)}
                className={COMPONENT_STYLES.form.input}
              />
            </div>
            
            <div>
              <label className={COMPONENT_STYLES.form.label}>
                {t('promotions:promotions.discount_type')}
              </label>
              <select
                value={formData.type}
                onChange={(e) => handleFormChange('type', e.target.value as 'percentage' | 'fixed_amount')}
                className={COMPONENT_STYLES.form.input}
              >
                <option value="percentage">{t('promotions:promotions.percentage')}</option>
                <option value="fixed_amount">{t('promotions:promotions.fixed_amount')}</option>
              </select>
            </div>
            
            <div>
              <label className={COMPONENT_STYLES.form.label}>
                {t('promotions:promotions.discount_value')}
              </label>
              <Flex align="center">
                <input
                  type="number"
                  min="0"
                  value={formData.value}
                  onChange={(e) => handleFormChange('value', parseFloat(e.target.value))}
                  className={COMPONENT_STYLES.form.input}
                />
                <span className="ml-2">
                  {formData.type === 'percentage' ? '%' : currencySymbol}
                </span>
              </Flex>
            </div>
          </Grid>
          
          <Grid 
            cols={isMobile ? 1 : 2} 
            gap="md" 
            className="mb-4"
            mobileCols={1}
          >
            <div className={COMPONENT_STYLES.form.checkbox.container}>
              <input
                type="checkbox"
                id="event-active"
                checked={formData.active}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFormChange('active', e.target.checked)}
                className={COMPONENT_STYLES.form.checkbox.input}
              />
              <label htmlFor="event-active" className={COMPONENT_STYLES.form.checkbox.label}>
                {t('promotions:promotions.active')}
              </label>
            </div>
            
            <div className={COMPONENT_STYLES.form.checkbox.container}>
              <input
                type="checkbox"
                id="event-recurring"
                checked={formData.recurring}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFormChange('recurring', e.target.checked)}
                className={COMPONENT_STYLES.form.checkbox.input}
              />
              <label htmlFor="event-recurring" className={COMPONENT_STYLES.form.checkbox.label}>
                {t('promotions:promotions.recurring')}
              </label>
            </div>
          </Grid>
          
          {formData.recurring && (
            <div className="mb-4">
              <label className={COMPONENT_STYLES.form.label}>
                {t('promotions:promotions.interval')}
              </label>
              <select
                value={formData.interval}
                onChange={(e) => handleFormChange('interval', e.target.value as 'weekly' | 'monthly' | 'yearly')}
                className={COMPONENT_STYLES.form.input}
              >
                <option value="weekly">{t('promotions:promotions.weekly')}</option>
                <option value="monthly">{t('promotions:promotions.monthly')}</option>
                <option value="yearly">{t('promotions:promotions.yearly')}</option>
              </select>
            </div>
          )}
          
          <Flex justify="end" gap="xs">
            <button
              onClick={handleCancel}
              className={`${COMPONENT_STYLES.button.base} ${COMPONENT_STYLES.button.sizes.sm} ${COMPONENT_STYLES.button.outline}`}
            >
              <X className="w-4 h-4 mr-1" />
              {t('common:cancel')}
            </button>
            
            <button
              onClick={handleSave}
              className={`${COMPONENT_STYLES.button.base} ${COMPONENT_STYLES.button.sizes.sm} ${COMPONENT_STYLES.button.primary}`}
            >
              <Check className="w-4 h-4 mr-1" />
              {t('common:save')}
            </button>
          </Flex>
        </Card>
      )}
      
      {/* Список событий */}
      {eventsList.length > 0 ? (
        <Table
          data={eventsList}
          columns={columns}
          scrollable={true}
          keyExtractor={(item) => item.name}
          compactMode={isMobile}
          mobileBehavior={isMobile ? "scroll" : "table"}
        />
      ) : (
        <Card padding="md" className="text-center bg-gray-50">
          <p className="text-gray-500">{t('promotions:promotions.no_seasonal_events')}</p>
          {!isAdding && (
            <button
              onClick={handleAddNew}
              className={`${COMPONENT_STYLES.button.base} ${COMPONENT_STYLES.button.sizes.sm} ${COMPONENT_STYLES.button.secondary} mt-2`}
            >
              <Plus className="w-4 h-4 mr-1" />
              {t('promotions:promotions.add_first_event')}
            </button>
          )}
        </Card>
      )}
    </Card>
  );
};

export default SeasonalEvents;